import { type ForwardedRef, forwardRef, useEffect } from 'react';

import {
  type ListCompanyEmailDomainsQuery,
  type ListCompanyEmailDomainsQueryVariables,
  useListCompanyEmailDomainsQuery,
} from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListCompanyEmailDomainsQueryVariables, 'pageNumber' | 'pageSize'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListCompanyEmailDomainsQuery['listCompanyEmailDomains']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListCompanyEmailDomainsQueryVariables, Data> => {
  const { data, ...rest } = useListCompanyEmailDomainsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      enabled: variables?.enabled ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listCompanyEmailDomains?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
      })) || [],
    ...rest,
  };
};

export const CompanyEmailDomainSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);
